// Local
import store from '@/store'
import i18n from '@/libs/i18n'
import { readBlob } from '@core/utils/utils'
import { getTenantContextInstance as tenantCtx } from '@/plugins/tenant'
import { ref, getDownloadURL, uploadString } from 'firebase/storage'

async function loadJsonFromStorage(currentPageRef, file) {
  try {
    const fileRef = ref(currentPageRef, file)
    const url = await getDownloadURL(fileRef)
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`HTTP error ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    // Check if the error is specifically because the file doesn't exist
    if (error.code === 'storage/object-not-found') {
      // This is expected for new pages. Return null to trigger the fallback.
      return null
    }

    // Log unexpected errors (e.g., Permission Denied)
    console.error('Error loading JSON from storage:', error)
    return null
  }
}

async function uploadFileToStorage(fileRef, content, contentType) {
  try {
    return await uploadString(fileRef, content, "raw", {
      contentType,
    });
  } catch (error) {
    console.log(error)
  }
}

/**
 * Bootstraps the Storage Manager
 *
 * @param {*} editor - The editor object.
 * @param {*} insertedPage - The current page object.
 *
 * @returns {void}
 */
export function bootstrapStorageManager(editor, insertedPage) {
  let currentPage = insertedPage || store.getters['pages/getCurrent']

  const storageManager = editor.Storage
  const { pagesStorage } = tenantCtx()
  const currentPageRef = ref(pagesStorage, currentPage.data.id)

  storageManager.add('google-cloud-storage', {
    async load() {
      console.log('LOADING PAGE RECIPE FROM CLOUD STORAGE')
      
      try {
        const recipe = await loadJsonFromStorage(
          currentPageRef,
          'data.json'
        )

        if (!recipe) {
          console.log('No recipe found, attempting to load Base Layout...');

          if (
            (currentPage.data?.baseLayout !== null && currentPage.data?.baseLayout !== undefined)
            && currentPage.data?.baseLayout?.status === 'pending'
          ) {
            try {
              const componentBlob = await store.dispatch('themes/fetchActiveThemeFile', currentPage.data.baseLayout)
              const componentValue = await readBlob(componentBlob)

              editor.setComponents(componentValue)

              await editor.store()

              store.dispatch('pages/updateCurrent', {
                'baseLayout.status': 'complete',
              }).then()
                .catch(error => console.debug(error))

              // Return null prevents GrapesJS from trying to parse the empty result
              return null
            } catch (error) {
              store.dispatch('pages/updateCurrent', {
                'baseLayout.status': 'error',
                'baseLayout.errorMessage': error,
              }).then()
                .catch(error => console.debug(error))

              store.dispatch('notify', {
                variant: 'danger',
                body: i18n.t('Something went wrong loading the base layout. Ignoring...'),
              })
            }
          }

          return null
        }

        return recipe
      } catch (error) {
        console.log('ERRR', error)
      }
    },

    /**
     * Stores the data in Google Cloud Storage and Firestore
     */
    async store() {
      // Get the current page data if the data has been edited during the opening of the page builder
      // and the execution of the store command
      currentPage = insertedPage || store.getters['pages/getCurrent']

      // Let us get the selected form ID to store it under `formId` as well
      const formId = editor.DomComponents.componentsById.formio?.getAttributes()?.form ?? null
      const counterId = editor.DomComponents.componentsById.counter?.getAttributes()?.counter ?? null

      if (formId && formId !== '' && formId !== 'undefined' && formId !== 'null') {
        const form = await store.dispatch('forms/fetchById', formId)

        if (formId && form?.data()?.deletedAt) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong saving the page. Selected form does not exists'),
            variant: 'danger',
          })

          return
        }
      }

      // If a counter is attached to the page, Inject the campaignCode into the page.
      if (counterId) {
        const counter = await store.dispatch('counters/fetchById', counterId)

        if (counter?.data()?.deletedAt) {
          store.dispatch('notify', {
            body: i18n.t('Something went wrong saving the page. Selected counter does not exists'),
            variant: 'danger',
          })

          return
        }

        // Only inject the campaignCode if it is filled in the counter.
        if (counter.data().campaignCode) {
          currentPage.data = {
            ...currentPage.data,
            campaignCode: counter.data().campaignCode,
          }
        }
      }

      const html = editor.getHtml()
      const css = editor.getCss({ avoidProtected: true })
      const projectData = editor.getProjectData()

      // Upload all three files in parallel
      const [dataJsonResult] = await Promise.all([
        uploadFileToStorage(
          ref(currentPageRef, 'data.json'),
          JSON.stringify(projectData),
          'application/json'
        ),
        uploadFileToStorage(
          ref(currentPageRef, 'index.html'),
          html,
          'text/html'
        ),
        uploadFileToStorage(
          ref(currentPageRef, 'styles.css'),
          css,
          'text/css'
        ),
      ])

      // Use data.json metadata for storageVersion (maintaining backward compatibility)
      const metadata = dataJsonResult?.metadata || {}

      store.dispatch('pages/updateCurrent', {
        ...currentPage.data,
        formId,
        counterId,
        storageVersion: {
          md5Hash: metadata.md5Hash,
          generation: metadata.generation,
        }
      })
    },
  })
}
